'use client';

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { eventsAPI, employeesAPI, salariesAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { TableSkeleton } from '../components/LoadingSkeleton';
import SearchBar from '../components/SearchBar';
import '../styles/Events.css';





function Events() {
  const { user } = useAuth();
  const { success, error: showError } = useToast();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    eventName: '',
    clientName: '',
    eventDate: '',
    numberOfPeople: '',
    totalBill: '',
    advanceReceived: '',
    status: 'upcoming',
  });

  // Assign staff to event (daily salary logs)
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignEvent, setAssignEvent] = useState(null);
  const [assignDate, setAssignDate] = useState('');
  const [employees, setEmployees] = useState([]);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
  const [employeeHeadcounts, setEmployeeHeadcounts] = useState({}); // { employeeId: number }
  const [assignLoading, setAssignLoading] = useState(false);
  
  // View assigned staff
  const [viewStaffOpen, setViewStaffOpen] = useState(false);
  const [viewStaffEvent, setViewStaffEvent] = useState(null);
  const [assignedStaff, setAssignedStaff] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);

  const [eventStaffCounts, setEventStaffCounts] = useState({}); // { eventId: count }

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const response = await eventsAPI.getAll();
      const eventsList = response.data.events;
      setEvents(eventsList);
      
      // Fetch staff counts for all events
      const counts = {};
      await Promise.all(
        eventsList.map(async (event) => {
          try {
            const staffRes = await salariesAPI.getByEvent(event._id);
            counts[event._id] = staffRes.data.salaries?.length || 0;
          } catch (err) {
            counts[event._id] = 0;
          }
        })
      );
      setEventStaffCounts(counts);
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to fetch events';
      showError(message);
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const fetchEmployees = async () => {
    try {
      const res = await employeesAPI.getAll();
      setEmployees(res.data?.employees || []);
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to fetch employees';
      showError(message);
    }
  };

  // Filter events based on search term
  const filteredEvents = useMemo(() => {
    if (!searchTerm) return events;
    const term = searchTerm.toLowerCase();
    return events.filter(event => 
      event.eventName.toLowerCase().includes(term) ||
      event.clientName.toLowerCase().includes(term) ||
      new Date(event.eventDate).toLocaleDateString().includes(term)
    );
  }, [events, searchTerm]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await eventsAPI.update(editingId, formData);
        success('Event updated successfully');
      } else {
        await eventsAPI.create(formData);
        success('Event created successfully');
      }
      setFormData({
        eventName: '',
        clientName: '',
        eventDate: '',
        numberOfPeople: '',
        totalBill: '',
        advanceReceived: '',
        status: 'upcoming',
      });
      setShowForm(false);
      setEditingId(null);
      fetchEvents();
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to save event';
      showError(message);
    }
  };

  const handleEdit = (event) => {
    setFormData({
      eventName: event.eventName,
      clientName: event.clientName,
      eventDate: event.eventDate.split('T')[0],
      numberOfPeople: event.numberOfPeople,
      totalBill: event.totalBill,
      advanceReceived: event.advanceReceived,
      status: event.status || 'upcoming',
    });
    setEditingId(event._id);
    setShowForm(true);
  };

  const handleStatusToggle = async (event) => {
    const newStatus = event.status === 'completed' ? 'upcoming' : 'completed';
    try {
      await eventsAPI.update(event._id, { ...event, status: newStatus });
      success(`Event marked as ${newStatus}`);
      fetchEvents();
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to update event status';
      showError(message);
    }
  };

  const handleDelete = async (id, eventName) => {
    if (window.confirm(`Are you sure you want to delete "${eventName}"? This action cannot be undone.`)) {
      try {
        await eventsAPI.delete(id);
        success('Event deleted successfully');
        fetchEvents();
      } catch (err) {
        const message = err.response?.data?.message || 'Failed to delete event';
        showError(message);
      }
    }
  };

  const openAssignStaff = async (event) => {
    setAssignEvent(event);
    // Set date to today (not event date, to avoid future date validation error)
    const today = new Date().toISOString().split('T')[0];
    setAssignDate(today);
    setSelectedEmployeeIds([]);
    setEmployeeHeadcounts({});
    setAssignOpen(true);
    if (employees.length === 0) {
      await fetchEmployees();
    }
  };

  const openViewStaff = async (event) => {
    setViewStaffEvent(event);
    setViewStaffOpen(true);
    await fetchAssignedStaff(event._id);
  };

  const closeViewStaff = () => {
    setViewStaffOpen(false);
    setViewStaffEvent(null);
    setAssignedStaff([]);
  };

  const fetchAssignedStaff = async (eventId) => {
    setStaffLoading(true);
    try {
      const response = await salariesAPI.getByEvent(eventId);
      setAssignedStaff(response.data.salaries || []);
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to fetch assigned staff';
      showError(message);
      setAssignedStaff([]);
    } finally {
      setStaffLoading(false);
    }
  };

  const handleTogglePaid = async (salaryId, currentStatus) => {
    try {
      await salariesAPI.update(salaryId, { isPaid: !currentStatus });
      success(`Salary marked as ${!currentStatus ? 'paid' : 'unpaid'}`);
      if (viewStaffEvent) {
        await fetchAssignedStaff(viewStaffEvent._id);
      }
      fetchEvents(); // Refresh to update counts
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to update salary status';
      showError(message);
    }
  };

  const handleRemoveAssignment = async (salaryId, employeeName) => {
    if (!window.confirm(`Remove "${employeeName}" from this event assignment?`)) {
      return;
    }
    try {
      await salariesAPI.delete(salaryId);
      success('Assignment removed successfully');
      if (viewStaffEvent) {
        await fetchAssignedStaff(viewStaffEvent._id);
      }
      fetchEvents(); // Refresh to update counts
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to remove assignment';
      showError(message);
    }
  };

  const closeAssignStaff = () => {
    if (assignLoading) return;
    setAssignOpen(false);
    setAssignEvent(null);
    setAssignDate('');
    setSelectedEmployeeIds([]);
  };

  const toggleEmployee = (employeeId) => {
    setSelectedEmployeeIds(prev => (
      prev.includes(employeeId) ? prev.filter(id => id !== employeeId) : [...prev, employeeId]
    ));
    setEmployeeHeadcounts(prev => {
      if (prev[employeeId]) return prev;
      return { ...prev, [employeeId]: 1 };
    });
  };

  const selectedTotal = useMemo(() => {
    if (!selectedEmployeeIds.length) return 0;
    const map = new Map(employees.map(e => [e._id, e]));
    return selectedEmployeeIds.reduce((sum, id) => {
      const emp = map.get(id);
      const daily = emp?.dailySalary ?? emp?.monthlySalary ?? 0;
      const hc = Number(employeeHeadcounts[id] || 1);
      return sum + Number(daily || 0) * hc;
    }, 0);
  }, [employees, selectedEmployeeIds, employeeHeadcounts]);

  const handleAssignSubmit = async () => {
    if (!assignEvent?._id) return;
    if (!assignDate) {
      showError('Please select a work date');
      return;
    }
    if (selectedEmployeeIds.length === 0) {
      showError('Please select at least one employee');
      return;
    }

    setAssignLoading(true);
    try {
      await Promise.all(
        selectedEmployeeIds.map(employeeId =>
          salariesAPI.create({
            employeeId,
            eventId: assignEvent._id,
            workDate: assignDate,
            headcount: employeeHeadcounts[employeeId] || 1,
          })
        )
      );
      success(`Assigned ${selectedEmployeeIds.length} employee(s) to "${assignEvent.eventName}"`);
      closeAssignStaff();
      fetchEvents(); // Refresh to update counts
    } catch (err) {
      const data = err.response?.data;
      const errors = Array.isArray(data?.errors) ? data.errors : [];
      const fieldMessages = errors
        .map(e => (e?.field ? `${e.field}: ${e.message}` : e?.message))
        .filter(Boolean);

      const message =
        fieldMessages.join(' | ') ||
        data?.message ||
        'Failed to assign employees';

      // Helpful client-side debug
      // eslint-disable-next-line no-console
      console.error('Assign staff failed:', { payload: { eventId: assignEvent?._id, workDate: assignDate, selectedEmployeeIds }, response: data });

      showError(message);
    } finally {
      setAssignLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({
      eventName: '',
      clientName: '',
      eventDate: '',
      numberOfPeople: '',
      totalBill: '',
      advanceReceived: '',
    });
  };

  const activeEmployees = useMemo(() => {
    return (employees || [])
      .filter(e => e.isActive)
      .sort((a, b) => {
        const ac = (a.category || 'other').localeCompare(b.category || 'other');
        if (ac !== 0) return ac;
        return (a.name || '').localeCompare(b.name || '');
      });
  }, [employees]);

  return (
    <div className="events-container">
      <div className="page-header">
        <h1>Events / Orders</h1>
        {user?.role === 'owner' && (
          <button onClick={() => setShowForm(!showForm)} className="btn">
            {showForm ? 'Cancel' : '+ New Event'}
          </button>
        )}
      </div>

      {!loading && events.length > 0 && (
        <SearchBar
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="Search by event name, client, or date..."
        />
      )}

      {showForm && (
        <div className="form-container">
          <h2>{editingId ? 'Edit Event' : 'Create New Event'}</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-grid">
              <div className="form-group">
                <label>Event Name *</label>
                <input
                  type="text"
                  name="eventName"
                  value={formData.eventName}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Client Name *</label>
                <input
                  type="text"
                  name="clientName"
                  value={formData.clientName}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Event Date *</label>
                <input
                  type="date"
                  name="eventDate"
                  value={formData.eventDate}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Number of People *</label>
                <input
                  type="number"
                  name="numberOfPeople"
                  value={formData.numberOfPeople}
                  onChange={handleInputChange}
                  required
                  min="1"
                />
              </div>
              <div className="form-group">
                <label>Total Bill *</label>
                <input
                  type="number"
                  name="totalBill"
                  value={formData.totalBill}
                  onChange={handleInputChange}
                  required
                  min="0"
                  step="0.01"
                />
              </div>
              <div className="form-group">
                <label>Advance Received *</label>
                <input
                  type="number"
                  name="advanceReceived"
                  value={formData.advanceReceived}
                  onChange={handleInputChange}
                  required
                  min="0"
                  step="0.01"
                />
              </div>
              {editingId && (
                <div className="form-group">
                  <label>Status *</label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="upcoming">Upcoming</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
              )}
            </div>
            <div className="form-actions">
              <button type="submit" className="btn">
                {editingId ? 'Update' : 'Create'} Event
              </button>
              <button type="button" onClick={handleCancel} className="btn btn-secondary">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {assignOpen && (
        <div className="modal-backdrop" onClick={closeAssignStaff}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h2>Assign Staff</h2>
                <div className="modal-subtitle">
                  {assignEvent?.eventName} • {assignEvent?.clientName}
                </div>
              </div>
              <button className="modal-close" onClick={closeAssignStaff} aria-label="Close">
                ✕
              </button>
            </div>

            <div className="modal-body">
              <div className="assign-grid">
                <div className="form-group">
                  <label>Work Date *</label>
                  <input
                    type="date"
                    value={assignDate}
                    onChange={(e) => setAssignDate(e.target.value)}
                    required
                    max={new Date().toISOString().split('T')[0]}
                  />
                  <div className="hint">This will create one daily salary entry per selected employee.</div>
                </div>

                <div className="employee-picker">
                  <div className="employee-picker-header">
                    <div className="employee-picker-title">Select Employees</div>
                    <div className="employee-picker-meta">
                      Selected: {selectedEmployeeIds.length} • Total: ₹{selectedTotal.toFixed(2)}
                    </div>
                  </div>

                  {activeEmployees.length === 0 ? (
                    <div className="no-data" style={{ marginTop: '10px' }}>
                      No active employees found. Please add employees first.
                    </div>
                  ) : (
                    <div className="employee-list">
                      {activeEmployees.map(emp => {
                        const daily = emp?.dailySalary ?? emp?.monthlySalary ?? 0;
                        const checked = selectedEmployeeIds.includes(emp._id);
                        const hc = employeeHeadcounts[emp._id] || 1;
                        return (
                          <label key={emp._id} className={`employee-item ${checked ? 'selected' : ''}`}>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleEmployee(emp._id)}
                            />
                            <div className="employee-item-main">
                              <div className="employee-item-title">
                                <span className="employee-name">{emp.name}</span>
                                <span className={`category-pill category-${emp.category || 'other'}`}>
                                  {emp.category || 'other'}
                                </span>
                              </div>
                              <div className="employee-item-sub">
                                <span className="employee-role">{emp.role}</span>
                                <span className="employee-salary">
                                  ₹{Number(daily).toFixed(2)}/day
                                </span>
                              </div>
                              <div className="employee-item-sub" style={{ marginTop: '6px' }}>
                                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <span>Team size</span>
                                  <input
                                    type="number"
                                    min="1"
                                    max="200"
                                    value={hc}
                                    disabled={!checked}
                                    onChange={(e) => {
                                      const v = Math.max(1, Math.min(200, parseInt(e.target.value || '1', 10) || 1));
                                      setEmployeeHeadcounts(prev => ({ ...prev, [emp._id]: v }));
                                    }}
                                    style={{ width: '72px', padding: '6px 8px' }}
                                  />
                                  <span style={{ fontWeight: 700, color: 'var(--dark-text)' }}>
                                    Total: ₹{(Number(daily || 0) * hc).toFixed(2)}
                                  </span>
                                </span>
                              </div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={closeAssignStaff} disabled={assignLoading}>
                Cancel
              </button>
              <button className="btn" onClick={handleAssignSubmit} disabled={assignLoading || activeEmployees.length === 0}>
                {assignLoading ? 'Assigning...' : 'Assign Staff'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Assigned Staff Modal */}
      {viewStaffOpen && (
        <div className="modal-backdrop" onClick={closeViewStaff}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h2>Assigned Staff</h2>
                <div className="modal-subtitle">
                  {viewStaffEvent?.eventName} • {viewStaffEvent?.clientName}
                </div>
              </div>
              <button className="modal-close" onClick={closeViewStaff} aria-label="Close">
                ✕
              </button>
            </div>

            <div className="modal-body">
              {staffLoading ? (
                <div className="no-data">Loading...</div>
              ) : assignedStaff.length === 0 ? (
                <div className="no-data">No employees assigned to this event yet.</div>
              ) : (
                <div className="assigned-staff-list">
                  {assignedStaff.map((salary) => {
                    const emp = salary.employeeId;
                    return (
                      <div key={salary._id} className="assigned-staff-item">
                        <div className="staff-item-main">
                          <div className="staff-item-header">
                            <span className="staff-name">{emp?.name || 'Unknown'}</span>
                            <span className={`status status-${salary.isPaid ? 'paid' : 'unpaid'}`}>
                              {salary.isPaid ? '✓ Paid' : '○ Unpaid'}
                            </span>
                          </div>
                          <div className="staff-item-details">
                            <span>{emp?.role || 'N/A'}</span>
                            <span>•</span>
                            <span>{new Date(salary.workDate).toLocaleDateString()}</span>
                            <span>•</span>
                            <span>Team: x{salary.headcount || 1}</span>
                            <span>•</span>
                            <span className="staff-amount">₹{salary.amount.toFixed(2)}</span>
                          </div>
                        </div>
                        <div className="staff-item-actions">
                          <button
                            onClick={() => handleTogglePaid(salary._id, salary.isPaid)}
                            className={`btn btn-small ${salary.isPaid ? 'btn-secondary' : 'btn-success'}`}
                            title={salary.isPaid ? 'Mark as unpaid' : 'Mark as paid'}
                          >
                            {salary.isPaid ? 'Mark Unpaid' : 'Mark Paid'}
                          </button>
                          <button
                            onClick={() => handleRemoveAssignment(salary._id, emp?.name || 'Employee')}
                            className="btn btn-danger btn-small"
                            title="Remove assignment"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={closeViewStaff}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <TableSkeleton rows={5} columns={8} />
      ) : filteredEvents.length > 0 ? (
        <div className="events-table-wrapper">
          <table className="events-table">
            <thead>
              <tr>
                <th>Event Name</th>
                <th>Client Name</th>
                <th>Date</th>
                <th>People</th>
                <th>Staff</th>
                <th>Total Bill</th>
                <th>Advance</th>
                <th>Balance</th>
                <th>Status</th>
                {user?.role === 'owner' && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {filteredEvents.map(event => (
                <tr key={event._id}>
                  <td>{event.eventName}</td>
                  <td>{event.clientName}</td>
                  <td>{new Date(event.eventDate).toLocaleDateString()}</td>
                  <td>{event.numberOfPeople}</td>
                  <td>
                    <span className="staff-count" title="Number of assigned employees">
                      {eventStaffCounts[event._id] || 0}
                    </span>
                  </td>
                  <td>₹{event.totalBill.toFixed(2)}</td>
                  <td>₹{event.advanceReceived.toFixed(2)}</td>
                  <td>₹{event.balanceAmount.toFixed(2)}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span className={`status status-${event.status}`}>
                        {event.status}
                      </span>
                      {user?.role === 'owner' && (
                        <button
                          onClick={() => handleStatusToggle(event)}
                          className="btn btn-small"
                          style={{ padding: '4px 8px', fontSize: '11px' }}
                          title={`Mark as ${event.status === 'completed' ? 'upcoming' : 'completed'}`}
                        >
                          {event.status === 'completed' ? '↩ Mark Upcoming' : '✓ Mark Completed'}
                        </button>
                      )}
                    </div>
                  </td>
                  {user?.role === 'owner' && (
                    <td>
                      <button
                        onClick={() => openViewStaff(event)}
                        className="btn btn-small"
                        title="View assigned employees"
                        style={{ marginRight: '5px' }}
                      >
                        👥 View Staff ({eventStaffCounts[event._id] || 0})
                      </button>
                      <button
                        onClick={() => openAssignStaff(event)}
                        className="btn btn-secondary btn-small"
                        title="Assign employees (daily salary) to this event"
                        style={{ marginRight: '5px' }}
                      >
                        + Assign
                      </button>
                      <button
                        onClick={() => handleEdit(event)}
                        className="btn btn-small"
                        style={{ marginRight: '5px' }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(event._id, event.eventName)}
                        className="btn btn-danger btn-small"
                      >
                        Delete
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : searchTerm ? (
        <div className="no-data">No events found matching "{searchTerm}"</div>
      ) : (
        <div className="no-data">No events found. Create your first event!</div>
      )}
    </div>
  );
}

export default Events;
